import React, { useState, useEffect } from 'react';
import {
  message,
} from 'antd';
import api from '../../../authentication/axiosInstance';
import styles from '../storm-strategy/StormStrategyPage.module.css';
import 'dayjs/locale/en';
import Header from '../../../components/client-care/bulk-storm-strategy/header/header';
import RegionSelectCard from '../../../components/client-care/bulk-storm-strategy/region-selection-card/region-selection-card';
import RegionsCard from '../../../components/client-care/bulk-storm-strategy/regions-card/regions-card';
import ReadyToSendCard from '../../../components/client-care/bulk-storm-strategy/ready-to-send-card/ready-to-send-card';
import RegionDetailsModal from '../../../components/client-care/bulk-storm-strategy/region-details-modal/region-details-modal';
import ConfirmationModal from '../../../components/client-care/bulk-storm-strategy/confirmation-modal/confirmation-modal';

// TODO: Fill these with the appropriate emails
// If TEST_MODE_EMAIL is empty, test mode will not work for this page
const TEST_MODE_EMAIL = ''; // Fill this with the email you'd like to send test emails to
const DEFAULT_CONTACT_EMAIL = ''; // Fill this with the default contact email

const BulkStormStrategyPage = () => {
  // Global state
  const [regions, setRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [regionData, setRegionData] = useState({});
  // When editing a region, store its key here (null if no modal open)
  const [selectedRegionForEdit, setSelectedRegionForEdit] = useState(null);
  const [isTestMode, setIsTestMode] = useState(false);
  const [loading, setLoading] = useState({
    regions: false,
    sending: false
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Toggle Test Mode with CTRL+ALT+T
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.ctrlKey && event.altKey && event.key === 't') {
        if (!TEST_MODE_EMAIL) {
          message.error('Test mode is not configured. Please set TEST_MODE_EMAIL first.');
          return;
        }
        setIsTestMode((prev) => {
          const newMode = !prev;
          message.success(`Test mode ${newMode ? 'enabled' : 'disabled'}`, 1);
          return newMode;
        });
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Fetch available regions on mount
  useEffect(() => {
    const fetchRegions = async () => {
      setLoading((prev) => ({ ...prev, regions: true }));
      try {
        const response = await api.get('/sites/site_regions/');
        setRegions(response.data);
      } catch (error) {
        console.error('Error fetching regions:', error);
        message.error('Failed to fetch regions');
      } finally {
        setLoading((prev) => ({ ...prev, regions: false }));
      }
    };
    fetchRegions();
  }, []);

  // When regions are selected, fetch their data (if not already done)
  useEffect(() => {
    const fetchRegionData = async (region) => {
      if (!region || regionData[region]?.loading) return;

      setRegionData((prev) => ({
        ...prev,
        [region]: { ...prev[region], loading: true, error: null }
      }));

      try {
        // fetch sites
        const sitesResponse = await api.get('/sites/', { params: { region } });
        const sites = sitesResponse.data.filter((site) => 
          site.region === region && 
          site.contract_status === 'Confirmed'
        );

        // fetch weather forecast
        let weather = null;
        let weatherError = null;
        
        try {
          const weatherResponse = await api.get(`/weatherworks/forecast/${region}/`);
          if (weatherResponse.data.success) {
            weather = weatherResponse.data.forecast;
          }
        } catch (error) {
          console.error(`Error fetching weather for ${region}:`, error);
          weatherError = error;
        }

        // if primary fetch failed, try Bolton
        if (!weather) {
          try {
            console.log('Attempting Bolton fallback...');
            const fallbackResponse = await api.get('/weatherworks/forecast/Bolton/');
            if (fallbackResponse.data.success) {
              weather = fallbackResponse.data.forecast;
              console.log('Using Bolton weather data as fallback');
            }
          } catch (fallbackError) {
            console.error('Error fetching Bolton fallback:', fallbackError);
            throw weatherError || fallbackError;
          }
        }

        if (!weather) {
          throw new Error('Failed to fetch weather data');
        }

        // fetch events
        const eventsResponse = await api.get(
          `/weatherworks/fetch-events-by-region/${encodeURIComponent(region)}/`
        );
        const events = eventsResponse.data.success ? eventsResponse.data.events : [];

        // only generate message if we have weather data
        let generatedMessage = '';
        if (weather) {
          try {
            const messageResponse = await api.post('/weatherworks/generate-message/', {
              region,
              weather_data: weather,
              events
            });
            generatedMessage = messageResponse.data.success ? messageResponse.data.message : '';
          } catch (messageError) {
            console.error(`Error generating message for ${region}:`, messageError);
          }
        }

        setRegionData((prev) => ({
          ...prev,
          [region]: {
            sites,
            selectedSites: [],
            weather,
            events,
            message: generatedMessage,
            loading: false,
            error: null,
            generating: false
          }
        }));
      } catch (error) {
        console.error(`Error fetching data for ${region}:`, error);
        setRegionData((prev) => ({
          ...prev,
          [region]: {
            ...prev[region],
            loading: false,
            error: error.message || 'Failed to fetch data'
          }
        }));
        message.error(`Failed to fetch data for ${region}`);
      }
    };

    selectedRegions.forEach((region) => {
      if (!regionData[region]) {
        fetchRegionData(region);
      }
    });
  }, [selectedRegions, regionData]);

  // Handlers
  const handleRegionSelect = (value) => {
    setSelectedRegions(value);
  };

  const handleSiteSelect = (region, site) => {
    setRegionData((prev) => ({
      ...prev,
      [region]: {
        ...prev[region],
        selectedSites: [...prev[region].selectedSites, site]
      }
    }));
  };

  const handleSiteRemove = (region, siteId) => {
    setRegionData((prev) => ({
      ...prev,
      [region]: {
        ...prev[region],
        selectedSites: prev[region].selectedSites.filter((site) => site.site_id !== siteId)
      }
    }));
  };

  const handleMessageChange = (region, value) => {
    setRegionData((prev) => ({
      ...prev,
      [region]: {
        ...prev[region],
        message: value
      }
    }));
  };

  const generateMessageForRegion = async (region) => {
    if (!regionData[region]?.weather) {
      message.error(`Weather data not available for ${region}`);
      return;
    }
    // Set generating flag
    setRegionData((prev) => ({
      ...prev,
      [region]: {
        ...prev[region],
        generating: true
      }
    }));
    try {
      const response = await api.post('/weatherworks/generate-message/', {
        region,
        weather_data: regionData[region].weather,
        events: regionData[region].events
      });
      if (response.data.success) {
        setRegionData((prev) => ({
          ...prev,
          [region]: {
            ...prev[region],
            message: response.data.message,
            generating: false
          }
        }));
        message.success(`Message generated for ${region}`);
      } else {
        throw new Error('Generation failed');
      }
    } catch (error) {
      console.error(`Error generating message for ${region}:`, error);
      message.error(`Failed to generate message for ${region}`);
      setRegionData((prev) => ({
        ...prev,
        [region]: {
          ...prev[region],
          generating: false
        }
      }));
    }
  };

  const handleGenerateAll = async () => {
    await Promise.all(
      selectedRegions.map((region) => {
        if (regionData[region]?.weather) {
          return generateMessageForRegion(region);
        }
        return Promise.resolve();
      })
    );
    message.success('Generated messages for all regions');
  };

  const handleSubmit = () => {
    const regionsData = selectedRegions
      .map((region) => ({
        region,
        sites: regionData[region]?.selectedSites || [],
        message: regionData[region]?.message
      }))
      .filter((data) => data.sites.length > 0 && data.message);

    if (regionsData.length === 0) {
      message.error('No valid regions to process');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setLoading((prev) => ({ ...prev, sending: true }));
    try {
      // first, fetch contacts for all sites
      const regionsData = await Promise.all(selectedRegions.map(async (region) => {
        const regionInfo = regionData[region];
        if (!regionInfo?.selectedSites?.length || !regionInfo?.message) {
          return null;
        }

        // fetch contacts for each site
        const sitesWithContacts = await Promise.all(regionInfo.selectedSites.map(async (site) => {
          try {
            const response = await api.get(`/sites/${site.site_id}/associated_contacts/`);
            return {
              site_id: site.site_id,
              site_name: site.site_name,
              contacts: isTestMode && TEST_MODE_EMAIL ? [{
                first_name: 'Test',
                last_name: 'Mode',
                email: TEST_MODE_EMAIL
              }] : (response.data?.length ? response.data : [{
                first_name: 'Default',
                last_name: 'Contact',
                email: DEFAULT_CONTACT_EMAIL || 'default@example.com'
              }])
            };
          } catch (error) {
            console.error(`Error fetching contacts for site ${site.site_id}:`, error);
            return {
              site_id: site.site_id,
              site_name: site.site_name,
              contacts: [{
                first_name: 'Default',
                last_name: 'Contact',
                email: DEFAULT_CONTACT_EMAIL || 'default@example.com'
              }]
            };
          }
        }));

        return {
          region,
          sites: sitesWithContacts,
          message: regionInfo.message,
          weather_data: regionInfo.weather,
          events: regionInfo.events || []
        };
      }));

      const validRegionsData = regionsData.filter(Boolean);

      if (validRegionsData.length === 0) {
        throw new Error('No valid regions to process');
      }

      const response = await api.post('/weatherworks/storm-strategies/bulk/', {
        regions: validRegionsData,
        test_mode: isTestMode
      });

      if (response.data && response.data.success) {
        const successCount = response.data.summary?.successful || 0;
        message.success(`Storm strategies sent successfully to ${successCount} regions`);
        setSelectedRegions([]);
        setRegionData({});
        setShowConfirmModal(false);
      } else {
        const errorMsg = response.data.results?.map(r => r.message).join(', ') || 'Failed to send storm strategies';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Error sending storm strategies:', error);
      message.error(error.message || 'Failed to send storm strategies');
    } finally {
      setLoading((prev) => ({ ...prev, sending: false }));
    }
  };

  return (
    <div className={styles.container}>
      <Header />
      <RegionSelectCard 
        handleGenerateAll={handleGenerateAll}
        selectedRegions={selectedRegions}
        handleRegionSelect={handleRegionSelect}
        loading={loading}
        regions={regions}
      />
      {selectedRegions.length > 0 && (
        <>
          <RegionsCard 
            selectedRegions={selectedRegions}
            regionData={regionData}
            setSelectedRegionForEdit={setSelectedRegionForEdit}
          />
          <ReadyToSendCard
            selectedRegions={selectedRegions}
            handleSubmit={handleSubmit}
            loading={loading}
          />
        </>
      )}
      <RegionDetailsModal
        selectedRegionForEdit={selectedRegionForEdit}
        setSelectedRegionForEdit={setSelectedRegionForEdit}
        regionData={regionData}
        setRegionData={setRegionData}
        handleSiteRemove={handleSiteRemove}
        handleSiteSelect={handleSiteSelect}
        generateMessageForRegion={generateMessageForRegion}
        handleMessageChange={handleMessageChange}
      />
      <ConfirmationModal
        showConfirmModal={showConfirmModal}
        setShowConfirmModal={setShowConfirmModal}
        handleConfirmSubmit={handleConfirmSubmit}
        isTestMode={isTestMode}
        selectedRegions={selectedRegions}
        regionData={regionData}
      />
    </div>
  );
};

export default BulkStormStrategyPage;
